import { useState, useMemo, useEffect } from "react";
import CrossIcon from "../../../../assets/icons/maid-module-icons/CrossIcon.svg";
import BlueArrow from "../../../../assets/icons/maid-module-icons/BlueArrow.svg";
import { commonAPI } from "../../../../services/CommonAPIService";
import Document from "./Document";
import { ToastContainer, toast } from 'react-toastify';

const UpdateDocument = ({ show, onHide, document, staffId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [showDocumentSelector, setShowDocumentSelector] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState({
    id: document?.docId?.toString(),
    label: document?.docName,
    image: document?.thumbnailImageURL,
    code: document?.docCode,
  });
  
  //  Masking-related states 
  const [fullNumber, setFullNumber] = useState(document?.docNumber || "");
  const [displayNumber, setDisplayNumber] = useState("");
  const [showMasked, setShowMasked] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(true);
  
  const [docImage, setDocImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(document?.docURL || "");

  const myUserData = useMemo(() => {
    const auth = localStorage.getItem("userData");
    if (!auth) return null;
    return JSON.parse(auth);
  }, []);

  // UseEffect to fetch SHOW_IDPROOF_MASKED
useEffect(() => {
  const loadMaskedSetting = async () => {
    let userData = null;

    try {
      const storedUser = localStorage.getItem("userData");
      if (!storedUser) throw new Error("No user data found");

      try {
        userData = JSON.parse(storedUser);
      } catch (parseErr) {
        throw new Error("Invalid user data format");
      }

      const endPointVariableKey = {
        urlVariable: null,
        buildingId: userData?.buildingId || null,
      };

      const response = await commonAPI(
        "AppSettings",
        undefined,
        endPointVariableKey
      );

      if (response?.success === true && Array.isArray(response.data)) {
        const setting = response.data.find(
          (item) => item.key === "SHOW_IDPROOF_MASKED"
        );

        let value = setting?.value;

        const isMasked =
          value === true ||
          value === "true" ||
          value === 1 ||
          value === "1" ||
          String(value).toLowerCase() === "true";

        setShowMasked(isMasked);
      } else {
        console.warn("AppSettings returned invalid data", response);
        setShowMasked(false);
      }

    } catch (err) {
      console.error("Failed to load SHOW_IDPROOF_MASKED setting:", err);

      //  Error Logging
      const bodyError = {
        userId: userData?.userId || null,
        mobileNumber: userData?.mobileNumber || null,
        requestJSON: null,
        uri: "/api/Master/AppSettings",
        errorSource: "CuroNextPortal",
        errorNumber: "UPDOC01",
        errorSummary: err?.message,
        errorDescription: err?.message,
        fileClassName: "UpdateDocument.js",
        functionName: "loadMaskedSetting",
      };

      try {
        await commonAPI("ErrorLogMaster", bodyError);
      } catch (logErr) {
        console.error("Error while logging error:", logErr);
      }

      setShowMasked(false);
    } finally {
      setSettingsLoading(false);
    }
  };

  if (show) {
    loadMaskedSetting();
  }
}, [show]);

  // UseEffect to initialize display value 
  useEffect(() => {
    if (document?.docNumber && !settingsLoading) {
      const docNum = document.docNumber.toString();
      
      if (showMasked) {
        // In masked mode, we only show/store last 4 digits
        const last4 = docNum.slice(-4);
        setFullNumber(last4);
        setDisplayNumber(last4);
      } else {
        // In full mode, show the full number
        setFullNumber(docNum);
        setDisplayNumber(docNum);
      }
    }
  }, [document?.docNumber, showMasked, settingsLoading]);

  if (!show) return null;

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setDocImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
    });
  };

  // handleNumberChange
  const handleNumberChange = (e) => {
    let value = e.target.value.trim();

    const docId = selectedDocument?.id?.toLowerCase() || "";
    const docName = (selectedDocument?.label || selectedDocument?.code || "").toLowerCase();

    let maxFullLength = 20;
    let allowedRegex = /[^0-9]/g;
    let forceUpper = false;

    if (docId === "aadhaar" || docName.includes("aadhar") || docName.includes("aadhaar")) {
      maxFullLength = 12;
      allowedRegex = /[^0-9]/g;
    } else if (docId === "pan" || docName.includes("pan")) {
      maxFullLength = 10;
      allowedRegex = /[^A-Za-z0-9]/g;
      forceUpper = true;
    } else if (docName.includes("voter") || docName.includes("epic")) {
      maxFullLength = 10;
      allowedRegex = /[^A-Za-z0-9]/g;
      forceUpper = true;
    }

    value = value.replace(allowedRegex, "");
    if (forceUpper) value = value.toUpperCase();

    if (showMasked) {
          const realValue = value.slice(0, 4);
      setFullNumber(realValue);
      setDisplayNumber(realValue);
    } else {
      
      const realValue = value.slice(0, maxFullLength);
      setFullNumber(realValue);
      setDisplayNumber(realValue);
    }
  };

const handleSave = async () => {
  if (!myUserData) {
    alert("User data not found. Please log in again.");
    return;
  }

  if (!staffId || !document?.staffXRefDocId) {
    alert("Staff or document details not available.");
    return;
  }

  setLoading(true);

  let payload = null; 
  try {
    const valueToSend = {};
    const trimmedDocNumber = (fullNumber || "").toString().trim();

    const docId = selectedDocument?.id?.toLowerCase() || "";
    const docName = (selectedDocument?.label || "").toLowerCase();
    const isAadhaar =
      docId === "aadhaar" ||
      docName.includes("aadhar") ||
      docName.includes("aadhaar");
    const isPAN = docId === "pan" || docName.includes("pan");
    const isVoter =
      docName.includes("voter") || docName.includes("epic");

    if (!trimmedDocNumber) {
      toast.warning("Document number is required.");
      return;
    }

    if (showMasked) {
      if (trimmedDocNumber.length !== 4) {
        toast.warning("Please enter exactly 4 digits.");
        return;
      }
    } else {
      if (isAadhaar && trimmedDocNumber.length !== 12) {
        toast.warning("Aadhaar number must be exactly 12 digits.");
        return;
      } else if (isPAN && trimmedDocNumber.length !== 10) {
        toast.warning("PAN number must be exactly 10 characters.");
        return;
      } else if (isVoter && trimmedDocNumber.length !== 10) {
        toast.warning("Voter ID must be exactly 10 characters.");
        return;
      }
    }

    if (selectedDocument?.id !== document?.docId?.toString()) {
      valueToSend.docId = Number(selectedDocument.id);
    }

    if (
      trimmedDocNumber !==
      (document?.docNumber?.toString()?.trim() || "")
    ) {
      valueToSend.docNumber = trimmedDocNumber;
    }

    if (docImage instanceof File) {
      const base64Image = await convertToBase64(docImage);
      valueToSend.docBase64 = base64Image;
    }

    if (Object.keys(valueToSend).length === 0) {
      alert("No changes detected.");
      onHide();
      return;
    }

    const hasDocNumber = !!valueToSend.docNumber;
    const hasDocImage = !!valueToSend.docBase64;

    if (hasDocNumber && !hasDocImage) {
      toast.warning("Upload new image when changing document number.");
      return;
    }

    if (hasDocImage && !hasDocNumber) {
      toast.warning("Change document number when uploading new image.");
      return;
    }

    payload = {
      staffId: staffId,
      lstDocuments: [
        {
          docId: Number(selectedDocument.id),
          ...valueToSend,
        },
      ],
      modifiedUserId: String(staffId),
    };

    const response = await commonAPI(
      "UpdateServiceStaffDetail",
      payload,
      { buildingId: myUserData?.buildingId }
    );

    if (response?.success === true) {
      onSuccess();
      onHide();
    } else {
      alert(response?.message || "Failed to update document");
    }

  } catch (error) {
    console.error("Error updating document:", error);

    const bodyError = {
      userId: myUserData?.userId,
      mobileNumber: myUserData?.mobileNumber,
      requestJSON: payload ? JSON.stringify(payload) : null,
      uri: "/api/User/ServiceStaffV1",
      errorSource: "CuroNextPortal",
      errorNumber: "UPDOC02",
      errorSummary: error?.message,
      errorDescription: error?.message,
      fileClassName: "UpdateDocument.js",
      functionName: "handleSave",
    };

    try {
      await commonAPI("ErrorLogMaster", bodyError);
    } catch (logErr) {
      console.error("Error while logging error:", logErr);
    }

    alert("An error occurred. Please try again.");
  } finally {
    setLoading(false);
  }
};

  const handleDocumentTypeChange = (doc) => {
    setSelectedDocument(doc);
    setFullNumber("");
    setDisplayNumber("");
    setDocImage(null);
    setImagePreview("");
    setShowDocumentSelector(false);
  };

  let label = "Document Number";
  let placeholder = "Enter number";
  let inputMaxLength = showMasked ? 4 : 20;

  const docId = selectedDocument?.id?.toLowerCase() || "";
  const docName = (selectedDocument?.label || "").toLowerCase();

  if (docId === "aadhaar" || docName.includes("aadhar") || docName.includes("aadhaar")) {
    label = showMasked ? "Last 4 digits of Aadhaar Number" : "Aadhaar Number";
    placeholder = showMasked ? "1234" : "1234 5678 9012";
    inputMaxLength = showMasked ? 4 : 12;
  } else if (docId === "pan" || docName.includes("pan")) {
    label = showMasked ? "Last 4 digits of PAN" : "PAN Number";
    placeholder = showMasked ? "1234" : "ABCDE1234F";
    inputMaxLength = showMasked ? 4 : 10;
  } else if (docName.includes("voter") || docName.includes("epic")) {
    label = showMasked ? "Last 4 digits of Voter ID" : "Voter ID Number";
    placeholder = showMasked ? "1234" : "ABC1234567";
    inputMaxLength = showMasked ? 4 : 10;
  }


  if (settingsLoading) {
    return (
      <>
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1080,
          }}
        />
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1090,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 16px",
          }}
        >
          <div
            className="bg-white d-flex flex-column align-items-center justify-content-center"
            style={{
              width: "100%",
              maxWidth: 600,
              padding: 40,
              borderRadius: 18,
            }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading security settings...</span>
            </div>
            <div className="mt-3">Preparing document update...</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ToastContainer />
      {/* Backdrop */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 1080,
        }}
      />

      {/* Modal */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 1090,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "0 16px",
        }}
      >
        <div
          className="bg-white d-flex flex-column"
          style={{
            width: "100%",
            maxWidth: 600,
            height: "96vh",
            maxHeight: "calc(100vh - 32px)",
            borderRadius: 18,
            overflow: "hidden",
            fontFamily: "Inter, sans-serif",
          }}
        >
          {/* Header */}
          <div
            className="d-flex justify-content-between align-items-center text-white"
            style={{
              backgroundColor: "#1C80D2",
              padding: "12px 16px",
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <h5
                className="mb-0"
                style={{
                  fontSize: "clamp(14px, 4vw, 18px)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {showDocumentSelector ? "Select Document Type" : "Update Document"}
              </h5>
            </div>
            <button
              className="btn p-0 d-flex align-items-center justify-content-center bg-white"
              style={{
                width: 24,
                height: 24,
                borderRadius: 6,
                flexShrink: 0,
              }}
              onClick={onHide}
            >
              <img src={CrossIcon} alt="cross" />
            </button>
          </div>

          {/* Body */}
          <div
            className="flex-grow-1"
            style={{
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              padding: "16px",
            }}
          >
            {showDocumentSelector ? (
              <Document
                selectedDocument={selectedDocument}
                setSelectedDocument={handleDocumentTypeChange}
              />
            ) : (
              <>
                {/* Document Type */}
                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "clamp(12px, 2.5vw, 13px)",
                      fontWeight: 500,
                      color: "#6b7280",
                      marginBottom: 8,
                    }}
                  >
                    Document Type
                  </label>
                  <div
                    onClick={() => setShowDocumentSelector(true)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "12px 14px",
                      border: "1px solid #C4C4C4",
                      borderRadius: 8,
                      fontSize: "clamp(13px, 3vw, 14px)",
                      color: "#111827",
                      background: "#fff",
                      cursor: "pointer",
                      position: "relative",
                    }}
                  >
                    <img
                      src={selectedDocument?.image}
                      alt={selectedDocument?.code}
                      style={{
                        width: "clamp(30px, 6vw, 35px)",
                        height: "auto",
                        borderRadius: 6,
                        objectFit: "cover",
                      }}
                    />
                    <span>{selectedDocument?.label}</span>
                    <img
                      src={BlueArrow}
                      alt="change"
                      style={{
                        position: "absolute",
                        right: 14,
                        width: 16,
                        height: 16,
                        transform: "rotate(180deg)",
                      }}
                    />
                  </div>
                </div>

                {/*  Updated Document Number  */}
                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "clamp(12px, 2.5vw, 13px)",
                      fontWeight: 500,
                      color: "#6b7280",
                      marginBottom: 8,
                    }}
                  >
                    {label}
                  </label>
                  <input
                    type="text"
                    value={displayNumber}
                    onChange={handleNumberChange}
                    placeholder={placeholder}
                    maxLength={inputMaxLength}
                    style={{
                      width: "100%",
                      padding: "12px 14px",
                      border: "1px solid #C4C4C4",
                      borderRadius: 8,
                      fontSize: "clamp(13px, 3vw, 14px)",
                      color: "#111827",
                      background: "#fff",
                      letterSpacing: (docId === "aadhaar" && !showMasked) ? "0.35em" : "normal",
                      fontFamily: "monospace",
                      textTransform: (docId === "pan" || docName.includes("voter") || docName.includes("epic")) && !showMasked ? "uppercase" : "none",
                    }}
                  />
                </div>

                {/* Document Image */}
                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      fontSize: "clamp(12px, 2.5vw, 13px)",
                      fontWeight: 500,
                      color: "#6b7280",
                      marginBottom: 8,
                    }}
                  >
                    Document Image
                  </label>
                  <div
                    style={{
                      border: "1px solid #C4C4C4",
                      borderRadius: 8,
                      padding: 16,
                      background: "#fff",
                    }}
                  >
                    {imagePreview ? (
                      <div style={{ textAlign: "center" }}>
                        <img
                          src={imagePreview}
                          alt="Document"
                          style={{
                            width: "100%",
                            maxWidth: "300px",
                            height: "auto",
                            borderRadius: 6,
                            objectFit: "cover",
                            marginBottom: 16,
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        style={{
                          padding: "40px 20px",
                          color: "#6b7280",
                          fontSize: "clamp(12px, 2.5vw, 13px)",
                          textAlign: "center",
                          marginBottom: 16,
                        }}
                      >
                        No image uploaded
                      </div>
                    )}

                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      id="docImageInput"
                      style={{ display: "none" }}
                    />
                    <div style={{ textAlign: "center" }}>
                      <label
                        htmlFor="docImageInput"
                        style={{
                          display: "inline-block",
                          padding: "10px 20px",
                          background: "#1C80D2",
                          color: "#fff",
                          borderRadius: 8,
                          cursor: "pointer",
                          fontSize: "clamp(12px, 2.5vw, 13px)",
                          fontWeight: 500,
                          textAlign: "center",
                          width: "auto",
                          maxWidth: "200px",
                        }}
                      >
                        {imagePreview ? "Upload New Image" : "Upload Image"}
                      </label>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Buttons */}
          {!showDocumentSelector && (
            <div style={{ padding: "16px" }}>
              <div style={{ display: "flex", gap: 12 }}>
                <button
                  onClick={onHide}
                  className="btn"
                  style={{
                    flex: 1,
                    background: "#FFFFFF",
                    color: "#1C80D2",
                    borderRadius: 30,
                    padding: "clamp(12px, 3vw, 14px)",
                    fontWeight: 600,
                    fontSize: "clamp(14px, 3vw, 15px)",
                    border: "1px solid #1C80D2",
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="btn"
                  style={{
                    flex: 1,
                    background: "#1C80D2",
                    color: "#FFFFFF",
                    borderRadius: 30,
                    padding: "clamp(12px, 3vw, 14px)",
                    fontWeight: 600,
                    fontSize: "clamp(14px, 3vw, 15px)",
                    border: "none",
                  }}
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Update"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UpdateDocument;